import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Search, X } from "lucide-react";
import api from "../lib/api";

// G — Bang shortcuts
const BANGS = {
  "!w": (q) => `https://en.wikipedia.org/wiki/Special:Search?search=${encodeURIComponent(q)}`,
  "!wfr": (q) => `https://fr.wikipedia.org/wiki/Special:Search?search=${encodeURIComponent(q)}`,
  "!yt": (q) => `https://www.youtube.com/results?search_query=${encodeURIComponent(q)}`,
  "!g": (q) => `https://www.google.com/search?q=${encodeURIComponent(q)}`,
  "!ddg": (q) => `https://duckduckgo.com/?q=${encodeURIComponent(q)}`,
  "!so": (q) => `https://stackoverflow.com/search?q=${encodeURIComponent(q)}`,
  "!gh": (q) => `https://github.com/search?q=${encodeURIComponent(q)}`,
  "!r": (q) => `https://www.reddit.com/search/?q=${encodeURIComponent(q)}`,
  "!yt-omni": (q, nav) => { nav(`/tube?q=${encodeURIComponent(q)}`); return null; },
  "!ai": (q, nav) => { nav(`/ai?prompt=${encodeURIComponent(q)}`); return null; },
  "!map": (q, nav) => { nav(`/map?q=${encodeURIComponent(q)}`); return null; },
};

function maybeBang(q, nav) {
  // Returns:
  //   undefined → not a bang, caller should do a normal search
  //   ""        → bang handled internally (navigation already triggered)
  //   url       → bang URL to redirect to externally
  const m = q.match(/^(!\w[\w-]*)\s+(.*)$/);
  if (!m) return undefined;
  const [, bang, rest] = m;
  const fn = BANGS[bang.toLowerCase()];
  if (!fn) return undefined;
  const target = fn(rest.trim(), nav);
  return target === null ? "" : target;
}

export function SearchBar({ initial = "", size = "lg", autoFocus = false }) {
  const [q, setQ] = useState(initial);
  const [suggestions, setSuggestions] = useState([]);
  const [highlight, setHighlight] = useState(-1);
  const [showDrop, setShowDrop] = useState(false);
  const inputRef = useRef(null);
  const wrapRef = useRef(null);
  const navigate = useNavigate();
  const debounceRef = useRef(null);

  useEffect(() => { setQ(initial); }, [initial]);

  useEffect(() => {
    if (autoFocus) inputRef.current?.focus();
    const onKey = (e) => {
      if (e.key === "/" && document.activeElement?.tagName !== "INPUT") {
        e.preventDefault();
        inputRef.current?.focus();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [autoFocus]);

  // close dropdown on outside click
  useEffect(() => {
    const onDoc = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setShowDrop(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  // H — autocomplete
  useEffect(() => {
    const txt = q.trim();
    setHighlight(-1);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!txt || txt.startsWith("!")) {
      setSuggestions([]);
      return;
    }
    debounceRef.current = setTimeout(async () => {
      try {
        const { data } = await api.get("/search/suggest", { params: { q: txt } });
        setSuggestions(data.suggestions || []);
      } catch {
        setSuggestions([]);
      }
    }, 150);
    return () => debounceRef.current && clearTimeout(debounceRef.current);
  }, [q]);

  const doSearch = (query) => {
    const text = query.trim();
    if (!text) return;
    // Bang first
    const bangTarget = maybeBang(text, navigate);
    if (bangTarget === "") { setShowDrop(false); return; } // internal bang handled (navigate already called)
    if (bangTarget) {
      window.location.href = bangTarget;
      return;
    }
    setShowDrop(false);
    navigate(`/search?q=${encodeURIComponent(text)}&type=web`);
  };

  const submit = (e) => {
    e.preventDefault();
    if (highlight >= 0 && suggestions[highlight]) {
      doSearch(suggestions[highlight]);
    } else {
      doSearch(q);
    }
  };

  const onKeyDown = (e) => {
    if (e.key === "Enter") {
      // Always submit on Enter, even if dropdown is open
      e.preventDefault();
      if (showDrop && highlight >= 0 && suggestions[highlight]) {
        doSearch(suggestions[highlight]);
      } else {
        doSearch(q);
      }
      return;
    }
    if (!showDrop || suggestions.length === 0) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlight((h) => Math.min(suggestions.length - 1, h + 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlight((h) => Math.max(-1, h - 1));
    } else if (e.key === "Escape") {
      setShowDrop(false);
    }
  };

  const padY = size === "lg" ? "py-5" : "py-3";
  const textSize = size === "lg" ? "text-lg" : "text-base";
  const isBang = q.trim().startsWith("!");

  return (
    <form ref={wrapRef} onSubmit={submit} className="w-full relative" data-testid="omni-search-form">
      <div className="relative">
        <Search
          className="absolute left-5 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none"
          size={size === "lg" ? 22 : 18}
        />
        <input
          ref={inputRef}
          data-testid="search-input"
          value={q}
          onChange={(e) => { setQ(e.target.value); setShowDrop(true); }}
          onFocus={() => setShowDrop(true)}
          onKeyDown={onKeyDown}
          placeholder={isBang ? "!w Einstein  ·  !yt lofi  ·  !gh react…" : "Cherchez sans être tracé…   (essayez !w, !yt, !gh)"}
          className={`omni-search-input w-full ${padY} pl-14 pr-28 ${textSize} bg-white border border-[var(--omni-border)] rounded-full transition`}
          spellCheck="false"
          autoComplete="off"
        />
        {q && (
          <button
            type="button"
            data-testid="clear-search-btn"
            onClick={() => { setQ(""); setSuggestions([]); inputRef.current?.focus(); }}
            className="absolute right-14 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-700 p-1"
            aria-label="Clear"
          >
            <X size={18} />
          </button>
        )}
        <button
          type="submit"
          data-testid="search-submit-btn"
          aria-label="Rechercher"
          className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center justify-center w-10 h-10 rounded-full bg-[var(--omni-accent)] hover:bg-[var(--omni-accent-hover)] text-white transition shadow-sm"
        >
          <Search size={16} />
        </button>
      </div>

      {/* Suggestions dropdown */}
      {showDrop && suggestions.length > 0 && !isBang && (
        <div
          className="absolute left-0 right-0 mt-2 bg-white border border-[var(--omni-border)] rounded-2xl shadow-lg overflow-hidden z-50"
          data-testid="search-suggestions"
        >
          {suggestions.map((s, i) => (
            <button
              key={s + i}
              type="button"
              onMouseDown={(e) => { e.preventDefault(); doSearch(s); }}
              onMouseEnter={() => setHighlight(i)}
              data-testid={`search-suggestion-${i}`}
              className={`w-full text-left px-5 py-2.5 text-sm flex items-center gap-3 ${highlight === i ? "bg-[var(--omni-surface)]" : "hover:bg-[var(--omni-surface)]"}`}
            >
              <Search size={14} className="text-gray-400 flex-shrink-0" />
              <span className="truncate">{s}</span>
            </button>
          ))}
        </div>
      )}

      {/* Bang helper */}
      {showDrop && isBang && (
        <div className="absolute left-0 right-0 mt-2 bg-white border border-[var(--omni-border)] rounded-2xl shadow-lg overflow-hidden z-50 p-3" data-testid="bang-helper">
          <div className="text-xs font-bold text-gray-500 uppercase mb-2">Raccourcis « bang »</div>
          <div className="grid grid-cols-2 gap-1 text-xs">
            {[
              ["!w", "Wikipedia EN"],
              ["!wfr", "Wikipedia FR"],
              ["!yt", "YouTube"],
              ["!yt-omni", "OmniTube"],
              ["!g", "Google"],
              ["!ddg", "DuckDuckGo"],
              ["!gh", "GitHub"],
              ["!so", "Stack Overflow"],
              ["!r", "Reddit"],
              ["!ai", "OmniAI"],
              ["!map", "OmniMap"],
            ].map(([b, l]) => (
              <div key={b} className="flex items-center gap-2 px-2 py-1 rounded hover:bg-[var(--omni-surface)]">
                <code className="text-[10px] font-mono bg-black text-white rounded px-1.5 py-0.5">{b}</code>
                <span className="text-gray-600">{l}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </form>
  );
}
